import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  AlertOctagon,
  ArrowLeft,
  Briefcase,
  CheckCircle2,
  CircleDashed,
  Clock,
  Copy,
  Download,
  ExternalLink,
  Lock,
  ShieldAlert,
  ShieldX,
  Siren,
  XCircle,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Skeleton } from '@/components/ui/Skeleton'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { CopyableField } from '@/features/transactions/components/CopyableField'
import { formatDateTime, formatTimeAgo } from '@/lib/format'
import { cn } from '@/lib/cn'
import { useCanAny } from '@/lib/permissions'
import { useMe } from '@/features/auth/hooks'
import { useBlockDetail } from './hooks/useBlockDetail'
import { BlockStatusBadge } from './components/BlockStatusBadge'
import { ApproveRejectDialog } from './components/ApproveRejectDialog'
import { LiftDialog } from './components/LiftDialog'
import { RegulatoryWarning } from './components/RegulatoryWarning'
import type {
  BlockAuditEntry,
  BlockDetail,
  BlockSource,
  BlockStatus,
  BlockType,
} from './types'

const EVENT_ICON: Record<
  BlockAuditEntry['event'],
  { Icon: React.ComponentType<{ size?: number; className?: string }>; tone: string }
> = {
  requested: { Icon: Clock, tone: 'bg-status-pending' },
  approved: { Icon: CheckCircle2, tone: 'bg-status-success' },
  rejected: { Icon: ShieldX, tone: 'bg-status-failed' },
  activated: { Icon: AlertOctagon, tone: 'bg-status-failed' },
  lifted: { Icon: XCircle, tone: 'bg-muted-fg' },
  expired: { Icon: CircleDashed, tone: 'bg-muted-fg' },
}

/**
 * Block detail — robust rebuild.
 *
 * Layout: 12-col grid, 8-col reading column on the left, 4-col sticky
 * action rail on the right. Each column is independently scrollable so
 * the action rail remains usable even on long audit trails.
 *
 * Reading column (top to bottom):
 *   - Header card with risk-tier stripe + status badges + target +
 *     reason + key timestamps.
 *   - Lifecycle stepper (Requested → Approved → Active →
 *     Lifted/Rejected/Expired) — the active step is highlighted.
 *   - Block metadata grid (type, identifier, bank, effective period,
 *     bvn-hash, regulatory flag).
 *   - Related entities card (customer, account, case, alert).
 *   - Audit trail (chronological, with rich icons + detail body).
 *
 * Action rail (sticky):
 *   - State-aware primary CTA (Approve/Reject for requested, Lift for
 *     active, "Already closed" for terminal states).
 *   - Source context callout (alert/case/court order/EFCC/NFIU
 *     signage with deep links).
 *   - Maker / checker / lifter snapshot.
 */
export function BlockDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: block, isLoading, isError, error, refetch } = useBlockDetail(id)
  const { data: me } = useMe()
  const canApprove = useCanAny('blocks:approve')
  const canLift = useCanAny('blocks:lift')
  const canLiftRegulatory = useCanAny('blocks:lift_regulatory')

  const [decisionOpen, setDecisionOpen] = useState(false)
  const [decisionMode, setDecisionMode] = useState<'approve' | 'reject'>('approve')
  const [liftOpen, setLiftOpen] = useState(false)

  if (isLoading) return <DetailSkeleton />
  if (isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Couldn't load block</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <p className="text-sm text-muted-fg">{error.message}</p>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Retry
          </Button>
        </CardContent>
      </Card>
    )
  }
  if (!block) return null

  const blockType = (block.block_type ?? block.type ?? 'customer')
  const isMine = !!block.requested_by && me?.id === block.requested_by.user_id
  const isRequested =
    block.status === 'requested' || block.status === 'pending'
  const isActive = block.status === 'active'
  const liftBlocked = (block.is_regulatory ?? false) && !canLiftRegulatory
  const stripe = riskStripe(block.status, block.is_regulatory ?? false)

  return (
    <article className="flex flex-col gap-4">
      <Button asChild variant="ghost" size="sm" className="self-start">
        <Link to="/blocks">
          <ArrowLeft size={12} /> Back to blocks
        </Link>
      </Button>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_360px]">
        {/* ------ Reading column ----------------------------- */}
        <div className="flex min-w-0 flex-col gap-4">
          {/* Header */}
          <Card className="overflow-hidden">
            <span
              className={cn('block h-1 w-full', stripe)}
              aria-hidden
            />
            <CardContent className="space-y-3 py-4">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-primary">
                  Compliance · Account Holds
                </p>
                <span
                  className="text-[0.625rem] uppercase tracking-wider text-muted-fg"
                  aria-hidden
                >
                  ·
                </span>
                <h1 className="font-mono text-lg font-semibold tabular-nums">
                  {block.block_number}
                </h1>
                <button
                  type="button"
                  onClick={() =>
                    void navigator.clipboard?.writeText(block.block_number)
                  }
                  aria-label="Copy block number"
                  className="rounded p-0.5 text-muted-fg hover:bg-muted hover:text-fg"
                >
                  <Copy size={11} />
                </button>
                <BlockStatusBadge status={block.status} />
                <TypeBadge type={blockType} />
                {block.is_regulatory ? (
                  <Badge variant="critical" className="gap-1">
                    <ShieldAlert size={10} aria-hidden />
                    Regulatory order
                  </Badge>
                ) : null}
              </div>

              <p className="text-base font-semibold leading-tight">
                {block.target?.display_label ??
                  block.target_value ??
                  '—'}
              </p>

              <p className="max-w-prose whitespace-pre-line text-sm text-muted-fg">
                {block.reason}
              </p>

              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-fg">
                <span>
                  Requested{' '}
                  {formatTimeAgo(
                    block.requested_at ?? block.created_at ?? block.effective_from,
                  )}
                </span>
                <span>
                  by{' '}
                  {block.requested_by?.display_name ??
                    (block.requested_by_id
                      ? `user #${block.requested_by_id}`
                      : '—')}
                </span>
                <span aria-hidden>·</span>
                <Badge variant="default" className="capitalize">
                  {block.source.replace(/_/g, ' ')}
                </Badge>
                {block.source_reference ? (
                  <span className="font-mono">{block.source_reference}</span>
                ) : null}
              </div>
            </CardContent>
          </Card>

          {/* Lifecycle stepper */}
          <Card>
            <CardHeader>
              <CardTitle>Lifecycle</CardTitle>
            </CardHeader>
            <CardContent>
              <Stepper block={block} />
            </CardContent>
          </Card>

          {/* Metadata */}
          <Card>
            <CardHeader>
              <CardTitle>Block metadata</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Type">
                  <span className="capitalize">{blockType}</span>
                </Field>
                <Field label="Identifier">
                  <CopyableField
                    value={
                      block.target?.identifier ?? block.target_value ?? '—'
                    }
                  />
                </Field>
                {block.target?.bank_code && (
                  <Field label="Bank code">
                    <span className="font-mono">{block.target.bank_code}</span>
                  </Field>
                )}
                {block.target_account_id ? (() => {
                  // Account link needs UUID + NUBAN: the React route
                  // is /customers/{uuid}/accounts/{nuban}. The backend
                  // enrichment populates target_customer_public_id +
                  // target_account_nuban; only render when both are
                  // present so we don't ship a dead link.
                  const custUUID = block.target_customer_public_id
                  const nuban = block.target_account_nuban
                  return (
                    <Field label="Account">
                      {custUUID && nuban ? (
                        <Link
                          to={`/customers/${custUUID}/accounts/${nuban}`}
                          className="text-primary hover:underline"
                        >
                          {nuban}
                        </Link>
                      ) : (
                        <span className="text-muted-fg">
                          Account #{block.target_account_id}
                        </span>
                      )}
                    </Field>
                  )
                })() : null}
                {(() => {
                  // For customer/account blocks the customer is on the
                  // target itself; for value blocks (BVN/card/device/IP/
                  // terminal) the schema forbids target_customer_id, so
                  // we fall back to linked_customer_id which the backend
                  // resolves from the linked alert or case. The React
                  // /customers/:id route resolves by public_id (UUID),
                  // never the numeric primary key — so prefer the
                  // public_id field, fall back to a non-linkable span
                  // if the backend didn't enrich.
                  const custUUID =
                    block.target_customer_public_id ??
                    block.linked_customer_public_id ??
                    null
                  const fallbackID =
                    block.target_customer_id ??
                    block.target?.customer_id ??
                    block.linked_customer_id ??
                    null
                  if (!custUUID && !fallbackID) return null
                  const isLinked =
                    !block.target_customer_id && !block.target?.customer_id
                  // Prefer the backend-enriched display name; fall back
                  // to the legacy target label or a generic "Customer #N".
                  const displayName =
                    (isLinked
                      ? block.linked_customer_display_name
                      : block.target_customer_display_name) ??
                    block.target?.display_label ??
                    (fallbackID ? `Customer #${fallbackID}` : 'Customer')
                  const custNumber =
                    (isLinked
                      ? block.linked_customer_number
                      : block.target_customer_number) ?? null
                  return (
                    <Field label="Customer">
                      <span className="flex flex-wrap items-baseline gap-x-2">
                        {custUUID ? (
                          <Link
                            to={`/customers/${custUUID}`}
                            className="font-medium text-primary hover:underline"
                          >
                            {displayName}
                          </Link>
                        ) : (
                          <span className="font-medium text-muted-fg">
                            {displayName}
                          </span>
                        )}
                        {custNumber ? (
                          <span className="font-mono text-[0.65rem] text-muted-fg">
                            {custNumber}
                          </span>
                        ) : null}
                        {isLinked ? (
                          <span className="text-[0.65rem] text-muted-fg">
                            (via linked {block.alert_id ? 'alert' : 'case'})
                          </span>
                        ) : null}
                      </span>
                    </Field>
                  )
                })()}
                <Field label="Effective from">
                  {formatDateTime(block.effective_from)}
                </Field>
                <Field label="Effective to">
                  {block.effective_to
                    ? formatDateTime(block.effective_to)
                    : isActive
                      ? 'Indefinite — active until lifted'
                      : '—'}
                </Field>
                {block.is_regulatory ? (
                  <Field label="Authority">
                    <span className="font-medium">
                      {regulatoryLabel(block.source)}
                    </span>
                  </Field>
                ) : null}
              </dl>
            </CardContent>
          </Card>

          {/* Related entities */}
          {(block.case_id || block.alert_id) ? (
            <Card>
              <CardHeader>
                <CardTitle>Related entities</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-2 sm:grid-cols-2">
                {block.case_id ? (() => {
                  // /cases/:id resolves by public_id (UUID), not the
                  // numeric primary key. Prefer case_public_id; fall
                  // back to a non-linkable chip when the backend
                  // enrichment didn't populate it. Show case_number +
                  // summary + status so the chip is informative even
                  // before the user clicks through.
                  const label = block.case_number ?? `Case #${block.case_id}`
                  const inner = (
                    <>
                      <span className="flex min-w-0 items-start gap-2">
                        <Briefcase size={14} className="mt-0.5 shrink-0 text-status-pending" />
                        <span className="min-w-0">
                          <span className="block text-[0.625rem] uppercase tracking-wider text-muted-fg">
                            Case{block.case_status ? ` · ${block.case_status.replaceAll('_', ' ')}` : ''}
                          </span>
                          <span className="block font-mono text-sm">{label}</span>
                          {block.case_summary ? (
                            <span className="block truncate text-xs text-muted-fg">
                              {block.case_summary}
                            </span>
                          ) : null}
                        </span>
                      </span>
                      {block.case_public_id ? (
                        <ExternalLink size={12} className="shrink-0 text-muted-fg" />
                      ) : null}
                    </>
                  )
                  return block.case_public_id ? (
                    <Link
                      to={`/cases/${block.case_public_id}`}
                      className="flex items-center justify-between gap-2 rounded border border-border bg-card p-3 hover:border-primary/40"
                    >
                      {inner}
                    </Link>
                  ) : (
                    <div className="flex items-center justify-between gap-2 rounded border border-border bg-card p-3">
                      {inner}
                    </div>
                  )
                })() : null}
                {block.alert_id ? (() => {
                  const label = block.alert_number ?? `Alert #${block.alert_id}`
                  const tail =
                    [block.alert_priority, block.alert_status]
                      .filter(Boolean)
                      .map((s) => s!.replaceAll('_', ' '))
                      .join(' · ')
                  const inner = (
                    <>
                      <span className="flex min-w-0 items-start gap-2">
                        <Siren size={14} className="mt-0.5 shrink-0 text-status-failed" />
                        <span className="min-w-0">
                          <span className="block text-[0.625rem] uppercase tracking-wider text-muted-fg">
                            Alert{tail ? ` · ${tail}` : ''}
                          </span>
                          <span className="block font-mono text-sm">{label}</span>
                          {block.alert_summary ? (
                            <span className="block truncate text-xs text-muted-fg">
                              {block.alert_summary}
                            </span>
                          ) : null}
                        </span>
                      </span>
                      {block.alert_public_id ? (
                        <ExternalLink size={12} className="shrink-0 text-muted-fg" />
                      ) : null}
                    </>
                  )
                  return block.alert_public_id ? (
                    <Link
                      to={`/alerts/${block.alert_public_id}`}
                      className="flex items-center justify-between gap-2 rounded border border-border bg-card p-3 hover:border-primary/40"
                    >
                      {inner}
                    </Link>
                  ) : (
                    <div className="flex items-center justify-between gap-2 rounded border border-border bg-card p-3">
                      {inner}
                    </div>
                  )
                })() : null}
              </CardContent>
            </Card>
          ) : null}

          {/* Audit trail */}
          <Card>
            <CardHeader>
              <CardTitle>Audit trail ({(block.audit ?? []).length})</CardTitle>
            </CardHeader>
            <CardContent>
              {(block.audit ?? []).length === 0 ? (
                <p className="text-xs text-muted-fg">
                  No audit entries yet.
                </p>
              ) : (
                <ol className="relative flex flex-col gap-3 border-l border-border pl-4">
                  {(block.audit ?? []).map((entry) => {
                    const { Icon, tone } = EVENT_ICON[entry.event]
                    return (
                      <li key={entry.id} className="relative">
                        <span
                          aria-hidden
                          className={cn(
                            'absolute -left-[1.32rem] top-1.5 flex h-2.5 w-2.5 items-center justify-center rounded-full ring-4 ring-card',
                            tone,
                          )}
                        />
                        <div className="flex items-start gap-2">
                          <Icon
                            size={12}
                            className="mt-0.5 shrink-0 text-muted-fg"
                            aria-hidden
                          />
                          <div className="flex flex-col">
                            <p className="text-sm">
                              <span className="font-medium capitalize">
                                {entry.event}
                              </span>
                              {entry.by && (
                                <span className="text-muted-fg">
                                  {' '}
                                  by {entry.by.display_name}
                                </span>
                              )}
                            </p>
                            {entry.detail && (
                              <p className="mt-0.5 text-xs text-muted-fg">
                                {entry.detail}
                              </p>
                            )}
                            <p className="mt-0.5 text-xs text-muted-fg">
                              {formatDateTime(entry.at)}
                            </p>
                          </div>
                        </div>
                      </li>
                    )
                  })}
                </ol>
              )}
            </CardContent>
          </Card>
        </div>

        {/* ------ Action rail -------------------------------- */}
        <aside className="flex flex-col gap-3 lg:sticky lg:top-4 lg:self-start">
          {/* Primary actions */}
          <Card>
            <CardContent className="flex flex-col gap-2 py-4">
              {isRequested && canApprove && !isMine && (
                <>
                  <Button
                    onClick={() => {
                      setDecisionMode('approve')
                      setDecisionOpen(true)
                    }}
                  >
                    <CheckCircle2 size={14} /> Approve
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setDecisionMode('reject')
                      setDecisionOpen(true)
                    }}
                  >
                    <ShieldX size={14} /> Reject
                  </Button>
                </>
              )}
              {isRequested && isMine && (
                <p className="text-xs text-muted-fg">
                  Awaiting an approver other than you. Maker-checker prevents
                  self-approval.
                </p>
              )}
              {isActive && canLift && !liftBlocked && (
                <Button variant="outline" onClick={() => setLiftOpen(true)}>
                  <XCircle size={14} /> Lift block
                </Button>
              )}
              {isActive && liftBlocked && <RegulatoryWarning />}
              {!isRequested && !isActive && (
                <Button variant="ghost" disabled className="justify-start">
                  <CircleDashed size={14} />
                  {block.status === 'lifted'
                    ? 'Lifted'
                    : block.status === 'rejected'
                      ? 'Rejected'
                      : block.status === 'expired'
                        ? 'Expired'
                        : block.status === 'approved'
                          ? 'Approved — awaiting effective window'
                          : 'Closed'}
                </Button>
              )}
              <button
                type="button"
                onClick={() => exportBlockJson(block)}
                className="mt-1 inline-flex items-center justify-center gap-1.5 rounded border border-border bg-card px-2 py-1.5 text-xs text-muted-fg hover:border-primary/40 hover:text-fg"
              >
                <Download size={11} aria-hidden /> Export JSON
              </button>
            </CardContent>
          </Card>

          {/* Source context */}
          <SourceContextCard block={block} />

          {/* Maker / checker / lifter */}
          <ActorsCard block={block} />
        </aside>
      </div>

      <ApproveRejectDialog
        open={decisionOpen}
        onOpenChange={setDecisionOpen}
        blockId={block.id}
        blockNumber={block.block_number}
        defaultMode={decisionMode}
        onResolved={() => void navigate('/blocks/approvals')}
      />
      <LiftDialog
        open={liftOpen}
        onOpenChange={setLiftOpen}
        blockId={block.id}
        blockNumber={block.block_number}
        isRegulatory={block.is_regulatory ?? false}
        onLifted={() => void refetch()}
      />
    </article>
  )
}

// ---- subcomponents -------------------------------------------------

function Stepper({ block }: { block: BlockDetail }) {
  const status = block.status
  const isApprovedTerm = status === 'approved'
  const isActiveTerm = status === 'active'
  const isLifted = status === 'lifted'
  const isExpired = status === 'expired'
  const isRejected = status === 'rejected'
  const requestedAt =
    block.requested_at ?? block.created_at ?? block.effective_from

  const steps: Array<{
    key: string
    label: string
    when: string | null | undefined
    state: 'done' | 'current' | 'todo' | 'failed'
  }> = [
    {
      key: 'requested',
      label: 'Requested',
      when: requestedAt,
      state: 'done',
    },
    {
      key: 'approved',
      label: isRejected ? 'Rejected' : 'Approved',
      when: isRejected
        ? block.rejected_at ?? null
        : (block.approved_at ?? null),
      state: isRejected
        ? 'failed'
        : status === 'requested' || status === 'pending'
          ? 'current'
          : 'done',
    },
    {
      key: 'active',
      label: 'Active',
      when: block.activated_at ?? block.effective_from,
      state: isRejected
        ? 'todo'
        : isActiveTerm
          ? 'current'
          : isApprovedTerm
            ? 'todo'
            : isLifted || isExpired
              ? 'done'
              : 'todo',
    },
    {
      key: 'closed',
      label: isLifted ? 'Lifted' : isExpired ? 'Expired' : 'Closed',
      when: block.lifted_at ?? block.expired_at ?? block.effective_to,
      state: isLifted || isExpired ? 'done' : 'todo',
    },
  ]

  return (
    <ol className="grid grid-cols-1 gap-3 sm:grid-cols-4">
      {steps.map((s, i) => (
        <li
          key={s.key}
          className={cn(
            'flex flex-col gap-1 rounded border p-2.5',
            s.state === 'current'
              ? 'border-primary bg-primary/5'
              : s.state === 'done'
                ? 'border-border'
                : s.state === 'failed'
                  ? 'border-status-failed/40 bg-status-failed/5'
                  : 'border-dashed border-border bg-muted/30',
          )}
        >
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'flex h-5 w-5 items-center justify-center rounded-full text-[0.625rem] font-semibold',
                s.state === 'done'
                  ? 'bg-status-success text-fg'
                  : s.state === 'current'
                    ? 'bg-primary text-primary-fg'
                    : s.state === 'failed'
                      ? 'bg-status-failed text-primary-fg'
                      : 'bg-muted text-muted-fg',
              )}
              aria-hidden
            >
              {i + 1}
            </span>
            <span
              className={cn(
                'text-sm font-medium',
                s.state === 'todo' && 'text-muted-fg',
              )}
            >
              {s.label}
            </span>
          </div>
          <p className="text-[0.625rem] text-muted-fg">
            {s.when
              ? formatDateTime(s.when)
              : s.state === 'current'
                ? 'In progress'
                : s.state === 'failed'
                  ? '—'
                  : 'Pending'}
          </p>
        </li>
      ))}
    </ol>
  )
}

function SourceContextCard({ block }: { block: BlockDetail }) {
  const reg = block.is_regulatory ?? false
  const tone = reg ? 'critical' : block.source === 'automated' ? 'info' : 'pending'
  return (
    <Card>
      <CardHeader>
        <CardTitle>Source context</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <Badge variant={tone} className="gap-1 capitalize">
          {reg ? <ShieldAlert size={10} aria-hidden /> : <Lock size={10} aria-hidden />}
          {block.source.replace(/_/g, ' ')}
        </Badge>
        {block.source_reference ? (
          <p className="break-all rounded bg-muted px-2 py-1 font-mono text-[0.6875rem]">
            {block.source_reference}
          </p>
        ) : null}
        <p className="text-xs text-muted-fg">{sourceDescription(block.source)}</p>
      </CardContent>
    </Card>
  )
}

function ActorsCard({ block }: { block: BlockDetail }) {
  const requested = block.requested_by?.display_name ??
    (block.requested_by_id ? `user #${block.requested_by_id}` : '—')
  const approver = block.approved_by?.display_name ??
    (block.approved_by_id ? `user #${block.approved_by_id}` : null)
  const lifter = block.lifted_by?.display_name ??
    (block.lifted_by_id ? `user #${block.lifted_by_id}` : null)
  return (
    <Card>
      <CardHeader>
        <CardTitle>Actors</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-xs">
        <ActorLine label="Requested by" who={requested} when={block.requested_at ?? block.created_at} />
        {approver && (
          <ActorLine label="Approved by" who={approver} when={block.approved_at} />
        )}
        {lifter && (
          <ActorLine label="Lifted by" who={lifter} when={block.lifted_at} />
        )}
      </CardContent>
    </Card>
  )
}

function ActorLine({
  label,
  who,
  when,
}: {
  label: string
  who: string
  when: string | null | undefined
}) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-border/40 pb-2 last:border-b-0 last:pb-0">
      <div className="flex flex-col">
        <span className="text-[0.625rem] uppercase tracking-wider text-muted-fg">
          {label}
        </span>
        <span className="text-sm">{who}</span>
      </div>
      {when ? (
        <span
          className="shrink-0 text-right text-[0.6875rem] tabular-nums text-muted-fg"
          title={formatDateTime(when)}
        >
          {formatTimeAgo(when)}
        </span>
      ) : null}
    </div>
  )
}

function TypeBadge({ type }: { type: BlockType }) {
  const map: Record<BlockType, React.ComponentProps<typeof Badge>['variant']> = {
    customer: 'info',
    account: 'info',
    bvn: 'pending',
    card: 'info',
    device: 'default',
    ip: 'default',
    terminal: 'pending',
  }
  return (
    <Badge variant={map[type] ?? 'default'} className="uppercase">
      {type}
    </Badge>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-border/40 py-1.5 last:border-b-0">
      <dt className="text-[0.625rem] uppercase tracking-wider text-muted-fg">
        {label}
      </dt>
      <dd className="text-sm">{children}</dd>
    </div>
  )
}

function DetailSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_360px]">
      <div className="flex flex-col gap-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
      <div className="flex flex-col gap-3">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    </div>
  )
}

// ---- helpers --------------------------------------------------------

function riskStripe(status: BlockStatus, regulatory: boolean): string {
  if (regulatory) return 'bg-priority-critical'
  if (status === 'active') return 'bg-status-failed'
  if (status === 'requested' || status === 'pending') return 'bg-status-pending'
  if (status === 'approved') return 'bg-primary'
  if (status === 'lifted') return 'bg-status-success'
  if (status === 'rejected') return 'bg-status-failed'
  return 'bg-muted-fg'
}

function regulatoryLabel(source: BlockSource): string {
  switch (source) {
    case 'court':
      return 'Court order (Mareva or freezing injunction)'
    case 'efcc':
      return 'EFCC directive'
    case 'nfiu':
      return 'NFIU consortium directive'
    case 'regulatory_order':
      return 'CBN regulatory order'
    default:
      return 'Regulatory'
  }
}

function sourceDescription(source: BlockSource): string {
  switch (source) {
    case 'court':
      return 'Court-ordered freeze. Cannot be lifted by ordinary maker-checker — requires legal review.'
    case 'efcc':
      return 'EFCC investigation directive. Lift requires authorised legal+compliance officer.'
    case 'nfiu':
      return 'NFIU consortium directive — cross-bank watchlist match.'
    case 'regulatory_order':
      return 'CBN-issued regulatory order; carries the same lift constraints as a court directive.'
    case 'automated':
      return 'Automatically applied by the rules engine — typically a velocity or denylist match.'
    case 'alert':
      return 'Block requested off the back of a triggering alert. Source reference points at the alert.'
    case 'case':
      return 'Block applied as part of an investigation. Lift on case closure (or independently if needed).'
    default:
      return 'Manually submitted by the analyst pool.'
  }
}

function exportBlockJson(block: BlockDetail) {
  try {
    const blob = new Blob([JSON.stringify(block, null, 2)], {
      type: 'application/json',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${block.block_number}.json`
    a.click()
    URL.revokeObjectURL(url)
  } catch {
    /* clipboard / blob path unavailable — silent. */
  }
}
